import type { LensCardData, LensFilterValues } from '~/types/leLens';

export const LTLP_MANIFEST_PAGE_ID = 'ltlp-manifest';

export type ReportView = 'bookings' | 'vendor-summary';
export const REPORT_VIEWS: ReadonlyArray<{ value: ReportView; label: string }> = [
  { value: 'bookings', label: 'Bookings' },
  { value: 'vendor-summary', label: 'Vendor Summary' },
];

/** The workbook's quick filters, declared in svc-reporting's native-specs/ltlp-manifest.json and keyed by filter id. */
export const FILTER_DEFINITIONS = [
  { key: 'product_subcategory', label: 'Product Subcategory Name' },
  { key: 'reservation_status', label: 'Reservation Status' },
  { key: 'item_status', label: 'Item Status' },
  { key: 'offer_id', label: 'Offer Bk' },
  { key: 'vendor', label: 'Vendor Name' },
  { key: 'exclude_old_margin_maxed', label: 'Exclude Old Margin Maxed Offers' },
] as const;
export type FilterKey = (typeof FILTER_DEFINITIONS)[number]['key'];

/** Each sheet's saved filter values in the workbook. */
const SAVED_SELECTIONS: Record<ReportView, Partial<Record<FilterKey, Array<string>>>> = {
  bookings: { product_subcategory: ['standard, Promo'], reservation_status: ['confirmed'] },
  'vendor-summary': { product_subcategory: ['standard, Promo'] },
};

/** The five measures both sheets show, in the order of the workbook's Measure Names filter. */
export const MEASURES = [
  { key: 'gross_sales_aud', label: 'Gross Sales Aud', currency: true },
  { key: 'margin_aud', label: 'Margin Aud', currency: true },
  { key: 'cost_sales_aud', label: 'Cost Sales Aud', currency: true },
  { key: 'packages', label: 'Number Of Packages', currency: false },
  { key: 'nights', label: 'Number Of Nights', currency: false },
] as const;
type MeasureKey = (typeof MEASURES)[number]['key'];
export type Measures = Record<MeasureKey, number | null>;

/** The Bookings sheet's row headers in source order, as the dataset's booking_row key carries them. */
export const BOOKING_FIELDS = [
  { key: 'transaction_date_sydt', label: 'Transaction Date Sydt' },
  { key: 'item_bk', label: 'Item Bk' },
  { key: 'vendor_name', label: 'Vendor Name' },
  { key: 'item_status', label: 'Item Status' },
  { key: 'reservation_status', label: 'Reservation Status' },
  { key: 'booking_number', label: 'Booking Number' },
  { key: 'supplier_booking_number', label: 'Supplier Booking Number' },
  { key: 'order_bk', label: 'Order Bk' },
  { key: 'customer_bk', label: 'Customer Bk' },
  { key: 'package_name', label: 'Package Name' },
  { key: 'check_in_date', label: 'Check In Date' },
  { key: 'check_out_date', label: 'Check Out Date' },
  { key: 'rate_plan_code', label: 'Rate Plan Code' },
  { key: 'rate_plan_name', label: 'Name (rate plan)' },
  { key: 'reservation_room_bk', label: 'Reservation Room Bk' },
] as const;
export type BookingField = (typeof BOOKING_FIELDS)[number]['key'];
type BookingRow = { key: string; fields: Record<BookingField, string | null>; measures: Measures };
type VendorMonthRow = { key: string; month: string; vendor: string; measures: Measures };

export type ManifestFilters = {
  reportView: ReportView;
  from: string;
  to: string;
  selections: Record<FilterKey, Array<string>>;
};
export type CardResult = { rows: Array<Record<string, unknown>>; truncated: boolean; card: LensCardData };
type CardProblem = { problem: string };

const sydneyDay = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(date);
const addDays = (day: string, amount: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + amount * 86400000).toISOString().slice(0, 10);
const monday = (day: string) => addDays(day, -((new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7));
/** The last complete Sydney day, the newest date reporting.sales holds in full. */
export const lastCompleteDay = (now = new Date()) => addDays(sydneyDay(now), -1);

/**
 * Bookings defaults to Tableau's "last week" (the previous complete Monday-to-Sunday week, Sydney time);
 * Vendor Summary to "this year" up to the last complete day.
 */
function defaultWindow(reportView: ReportView, now = new Date()): { from: string; to: string } {
  const today = sydneyDay(now);
  if (reportView === 'bookings') {
    const thisMonday = monday(today);
    return { from: addDays(thisMonday, -7), to: addDays(thisMonday, -1) };
  }
  const to = lastCompleteDay(now);
  return { from: `${to.slice(0, 4)}-01-01`, to };
}

export function defaultFilters(reportView: ReportView = 'bookings', now = new Date()): ManifestFilters {
  const selections = Object.fromEntries(
    FILTER_DEFINITIONS.map(({ key }) => [key, [...(SAVED_SELECTIONS[reportView][key] ?? [])]]),
  ) as Record<FilterKey, Array<string>>;
  return { reportView, ...defaultWindow(reportView, now), selections };
}

const isDay = (value: string | null): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && new Date(`${value}T00:00:00Z`).toISOString().startsWith(value);

export function filtersError(filters: ManifestFilters, now = new Date()): string | null {
  if (!isDay(filters.from) || !isDay(filters.to)) {
    return 'Choose valid dates in YYYY-MM-DD format.';
  }
  if (filters.from > filters.to) {
    return 'The start date must be on or before the end date.';
  }
  if (filters.to > lastCompleteDay(now)) {
    return `Sales are complete to ${lastCompleteDay(now)}. Choose an earlier end date.`;
  }
  return null;
}

export function parseFilters(params: URLSearchParams, now = new Date()): ManifestFilters {
  const requested = params.get('reportView');
  const reportView = REPORT_VIEWS.some((view) => view.value === requested) ? (requested as ReportView) : 'bookings';
  const filters = defaultFilters(reportView, now);
  if (isDay(params.get('from'))) {
    filters.from = params.get('from');
  }
  if (isDay(params.get('to'))) {
    filters.to = params.get('to');
  }
  for (const { key } of FILTER_DEFINITIONS) {
    if (!params.has(key)) {
      continue;
    }
    try {
      const value: unknown = JSON.parse(params.get(key));
      if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        filters.selections[key] = value;
      }
    } catch {
      // A malformed selection keeps the default.
    }
  }
  return filters;
}

export function writeFilters(filters: ManifestFilters, current: URLSearchParams, now = new Date()): URLSearchParams {
  const params = new URLSearchParams(current);
  const defaults = defaultFilters(filters.reportView, now);
  params.set('reportView', filters.reportView);
  for (const key of ['from', 'to'] as const) {
    if (filters[key] === defaults[key]) {
      params.delete(key);
    } else {
      params.set(key, filters[key]);
    }
  }
  for (const { key } of FILTER_DEFINITIONS) {
    const values = [...new Set(filters.selections[key])].sort();
    if (JSON.stringify(values) === JSON.stringify([...defaults.selections[key]].sort())) {
      params.delete(key);
    } else {
      params.set(key, JSON.stringify(values));
    }
  }
  return params;
}

/** An empty selection means every value. */
export function lensFilters(filters: ManifestFilters): LensFilterValues {
  const values: LensFilterValues = {
    native_view: filters.reportView,
    period: { window: { from: filters.from, to: filters.to }, comparison: null },
  };
  for (const { key } of FILTER_DEFINITIONS) {
    if (filters.selections[key].length) {
      values[key] = filters.selections[key];
    }
  }
  return values;
}

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(typeof value === 'object' && value && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};
const text = (value: unknown): string | null =>
  value === null || value === undefined
    ? null
    : String(typeof value === 'object' && 'value' in value ? value.value : value);

const measuresOf = (row: Record<string, unknown>): Measures =>
  Object.fromEntries(MEASURES.map(({ key }) => [key, num(row[key])])) as Measures;

/** Sums skip missing values; a measure with no known value stays null rather than zero. */
export function totalsOf(rows: ReadonlyArray<{ measures: Measures }>): Measures {
  return Object.fromEntries(
    MEASURES.map(({ key }) => {
      const known = rows.map((row) => row.measures[key]).filter((value): value is number => value !== null);
      return [key, known.length ? known.reduce((total, value) => total + value, 0) : null];
    }),
  ) as Measures;
}

export function readCard(cards: ReadonlyArray<LensCardData>, cardId: string): CardResult | CardProblem {
  const card = cards.find((item) => item.cardId === cardId);
  if (!card) {
    return { problem: 'This section is still loading.' };
  }
  if (card.status === 'ready' || card.status === 'empty') {
    return { rows: card.rows ?? [], truncated: Boolean(card.truncated), card };
  }
  if (card.status === 'restricted') {
    return { problem: card.message ?? 'You do not have access to this section.' };
  }
  return { problem: `This section could not load. ${card.message ?? ''}`.trim() };
}

export const isProblem = (result: CardResult | CardProblem): result is CardProblem => 'problem' in result;

const compareTuples = (a: ReadonlyArray<string | null>, b: ReadonlyArray<string | null>) => {
  for (let index = 0; index < a.length; index += 1) {
    const order = (a[index] ?? '').localeCompare(b[index] ?? '');
    if (order) {
      return order;
    }
  }
  return 0;
};

/** Sorted by the row headers in order, as Tableau sorts them. */
export function bookingRows(result: CardResult): Array<BookingRow> {
  return result.rows
    .map((row) => {
      const key = text(row.booking_row) ?? '{}';
      let parsed: Record<string, unknown> = {};
      try {
        const value: unknown = JSON.parse(key);
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          parsed = value as Record<string, unknown>;
        }
      } catch {
        // An unreadable key still shows its measures, with blank headers.
      }
      const fields = Object.fromEntries(BOOKING_FIELDS.map((field) => [field.key, text(parsed[field.key])])) as Record<
        BookingField,
        string | null
      >;
      return { key, fields, measures: measuresOf(row) };
    })
    .sort((a, b) =>
      compareTuples(
        BOOKING_FIELDS.map((field) => a.fields[field.key]),
        BOOKING_FIELDS.map((field) => b.fields[field.key]),
      ),
    );
}

export function vendorMonthRows(result: CardResult): Array<VendorMonthRow> {
  return result.rows
    .map((row) => {
      const month = (text(row.transaction_date_sydt ?? row['month(transaction_date_sydt)']) ?? '').slice(0, 7);
      const vendor = text(row.vendor) ?? 'Unknown';
      return { key: `${month}\u0000${vendor}`, month, vendor, measures: measuresOf(row) };
    })
    .sort((a, b) => a.month.localeCompare(b.month) || a.vendor.localeCompare(b.vendor));
}

export function monthLabel(month: string): string {
  if (!/^\d{4}-\d{2}$/.test(month)) {
    return month || 'Unknown';
  }
  return new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${month}-01T00:00:00Z`),
  );
}

export function measureText(value: number | null, currency: boolean): string {
  if (value === null || !Number.isFinite(value)) {
    return '';
  }
  return new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: currency ? 2 : 0,
    maximumFractionDigits: currency ? 2 : 0,
  }).format(value);
}

/** CSV of every loaded row, numbers unformatted, with text cells guarded against spreadsheet formulas. */
export function manifestCsv(
  title: string,
  period: string,
  headers: ReadonlyArray<string>,
  rows: ReadonlyArray<ReadonlyArray<string | number | null>>,
): string {
  const cell = (value: string | number | null) => {
    if (value === null) {
      return '';
    }
    if (typeof value === 'number') {
      return String(value);
    }
    const guarded = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
    return `"${guarded.replace(/"/g, '""')}"`;
  };
  const lines: Array<ReadonlyArray<string | number | null>> = [
    ['Report', `LTLP Manifest · ${title}`],
    ['Source', 'lux-analytics-prod.reporting.sales (LTLP, product_bk bedbank_marriott)'],
    ['Period', period],
    headers,
    ...rows,
  ];
  return '﻿' + lines.map((line) => line.map(cell).join(',')).join('\r\n');
}

export function periodLabel(card: LensCardData | undefined, fallback = ''): string {
  return card?.period?.label?.current ?? fallback;
}
